import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, ThumbsUp, ThumbsDown, Share2 } from "lucide-react";
import { useVideo } from "../contexts/VideoContext";

interface VideoPlayerProps {
  video: {
    id: string;
    title: string;
    artist: string;
    audioUrl: string;
    frames: string[];
    likes: number;
    dislikes: number;
    views: number;
  };
  filter?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, filter = "none" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const { likeVideo } = useVideo();
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && video.frames.length > 0) {
      interval = setInterval(() => {
        setCurrentFrame(prev => (prev + 1) % video.frames.length);
      }, 200); // Change frame every 200ms
    }
    return () => clearInterval(interval);
  }, [isPlaying, video.frames.length]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="video-player">
      <div className="video-container">
        {video.frames.length > 0 && (
          <div className="frame-display">
            <img 
              src={video.frames[currentFrame]} 
              alt={`Frame ${currentFrame}`}
              style={{ filter }}
              className="video-frame"
            />
            <div className="video-overlay">
              <button className="play-button" onClick={togglePlay}>
                {isPlaying ? <Pause size={48} /> : <Play size={48} />}
              </button>
            </div>
          </div>
        )}
        
        <audio
          ref={audioRef}
          src={video.audioUrl}
          onEnded={() => setIsPlaying(false)}
        />
      </div>

      <div className="video-info">
        <h2>{video.title}</h2>
        <p className="artist">by {video.artist}</p>
        <div className="video-stats">
          <span>{video.views.toLocaleString()} wyświetleń</span>
        </div>

        <div className="video-actions">
          <button onClick={() => likeVideo(video.id)} className="action-button">
            <ThumbsUp size={20} />
            {video.likes}
          </button>
          <button className="action-button">
            <ThumbsDown size={20} />
            {video.dislikes}
          </button>
          <button onClick={toggleMute} className="action-button">
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <button className="action-button">
            <Share2 size={20} />
            Udostępnij
          </button>
        </div>
      </div>
    </div>
  );
};
